import SvgGlobe from '@icons/globe-inverted.svg?react';
import React, { useEffect, useId, useState } from 'react';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import useTranslation from '@hooks/i18n/useTranslation.tsx';

const TIMEZONE_VISIBLE_KEY = 'coTimezone.visible';
const TIMEZONE_LABEL_VISIBLE_KEY = 'coTimezone.labelVisible';
const TIMEZONE_VISIBILITY_EVENT = 'coTimezone:visibility-changed';
const TIMEZONE_LABEL_VISIBILITY_EVENT = 'coTimezone:label-visibility-changed';

const readBool = (key: string) => {
  try {
    return window.localStorage.getItem(key) !== 'false';
  } catch {
    return true;
  }
};

const writeBool = (key: string, eventName: string, value: boolean) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
  }
  window.dispatchEvent(new CustomEvent<boolean>(eventName, { detail: value }));
};

const useBoolWithEvent = (key: string, eventName: string): [boolean, (v: boolean) => void] => {
  const [value, setValue] = useState<boolean>(() => readBool(key));
  useEffect(() => {
    const handler = (e: Event) => setValue((e as CustomEvent<boolean>).detail);
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [eventName]);
  return [value, (v: boolean) => writeBool(key, eventName, v)];
};

export const useTimezoneVisibility = () => useBoolWithEvent(TIMEZONE_VISIBLE_KEY, TIMEZONE_VISIBILITY_EVENT);
const useTimezoneLabelVisibility = () => useBoolWithEvent(TIMEZONE_LABEL_VISIBLE_KEY, TIMEZONE_LABEL_VISIBILITY_EVENT);

const HIGHLIGHT_TIMEZONE = (() => {
  try {
    return window.localStorage.getItem('coTimezone.highlight') === 'true';
  } catch {
    return false;
  }
})();

const BASE_CLASSES = "font-['DM_Sans'] text-xs font-normal leading-normal tracking-[0.24px]";
const VARIANT_CLASSES = HIGHLIGHT_TIMEZONE ? 'bg-red-500 text-white' : 'text-content-tertiary';
const SPAN_CLASSES = `${VARIANT_CLASSES} ${BASE_CLASSES}`;

interface ComponentProps {
  timezoneId: string;
  dataTestId?: string;
}

const COTimezone: React.FC<ComponentProps> = ({ timezoneId, dataTestId }) => {
  const { tr } = useTranslation();
  const reactId = useId();
  const targetId = `co-timezone-${reactId.replace(/:/g, '')}`;
  const [visible] = useTimezoneVisibility();
  const [labelVisible, setLabelVisible] = useTimezoneLabelVisibility();

  if (!timezoneId || timezoneId === 'null') return null;
  if (!visible) return null;

  const toggleLabel = () => setLabelVisible(!labelVisible);

  const tooltipContent = (
    <div className="flex flex-col gap-1">
      <span>{tr('COTimezone.tooltip', 'Timezone for related times is {{-timezoneId}}', { timezoneId })}</span>
      <span className="text-[0.7rem] opacity-70">
        {labelVisible
          ? tr('COTimezone.clickToCollapse', 'Click to collapse the timezone info')
          : tr('COTimezone.clickToExpand', 'Click to expand the timezone info')}
      </span>
    </div>
  );

  return (
    <>
      <div className="flex items-center">
        <div id={targetId} onClick={toggleLabel} className="inline-flex items-center gap-1 cursor-pointer" data-testid={dataTestId}>
          <SvgGlobe className="h-3.5 w-3.5 text-content-tertiary" />
          {labelVisible && (
            <span className={SPAN_CLASSES}>
              {timezoneId}
            </span>
          )}
        </div>
      </div>
      <CustomTooltip
        targetSelector={`#${targetId}`}
        content={tooltipContent}
      />
    </>
  );
}

export default COTimezone;
